import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { mediaQueryLarge, onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {HTMLElement & {
 *   getValue: () => string,
 *   setValue: (value: string) => void,
 *   onQuantityChange?: () => void,
 *   updateButtonStates?: () => void,
 *   canAddToCart?: () => { canAdd: boolean }
 * }} QuantitySelector
 */

/**
 * @typedef {{ id: number, options: string[], available: boolean }} VariantEntry
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's add to cart button
 * @property {HTMLSelectElement[]} [optionSelects] - One dropdown per option that has more than one value
 * @property {HTMLScriptElement} [variantData] - JSON list of the product's variants
 * @property {HTMLInputElement} [stickyQuantity] - Quantity input
 * @property {HTMLButtonElement} [stickyQuantityMinus] - Quantity decrease button
 * @property {HTMLButtonElement} [stickyQuantityPlus] - Quantity increase button
 * @property {HTMLImageElement} [productImage] - Product image element
 */

/** How long to wait for the main picker to finish one option change before moving on */
const VARIANT_UPDATE_TIMEOUT = 15000;

/** A tap held during a variant change is dropped if the change takes longer than this */
const PENDING_ADD_EXPIRY = 20000;

/**
 * A custom element that manages a sticky add-to-cart bar with its own option
 * dropdowns and quantity stepper. Shows when the main buy buttons scroll out of view.
 *
 * The bar never adds to cart itself: it drives the main variant picker and
 * clicks the main add to cart button, so the product form's own validation,
 * events and cart drawer behaviour all apply.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {string} */
  #requestedVariantId = '';

  /** @type {Promise<void> | null} */
  #variantChange = null;

  /** When a tap was held for a variant change to settle, 0 when none is held */
  #pendingAddAt = 0;

  /** Whether the held tap was on the notify-me button, so it replays only as the same action */
  #pendingNotify = false;

  /** @type {CustomEvent | null} */
  #deferredVariantEvent = null;

  /** @type {{ text: string, variants: VariantEntry[] }} */
  #variantCache = { text: '', variants: [] };

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange, { signal });

    this.#setQuantity(parseInt(this.dataset.initialQuantity || '1') || 1, { syncMain: false });
    this.#syncNotifyMeButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    this.#pendingAddAt = 0;
    this.#deferredVariantEvent = null;
    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = this.#getMainAddToCartButton();
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    // The main form re-renders its button on variant changes, so look it up fresh
    this.#targetAddToCartButton = this.#getMainAddToCartButton() ?? this.#targetAddToCartButton;
    if (!this.#targetAddToCartButton) return;

    // Mid variant change the main form can still hold an in-between
    // combination. Hold the tap and replay it once the change settles.
    if (this.#variantChange) {
      this.#pendingAddAt = Date.now();
      this.#pendingNotify = this.refs.addToCartButton.dataset.notifyMe === 'true';
      this.refs.addToCartButton.setAttribute('aria-busy', 'true');
      return;
    }

    // On a sold-out variant the button opens the back-in-stock form instead
    if (this.refs.addToCartButton.dataset.notifyMe === 'true') {
      this.#openNotifyMe();
      return;
    }

    // Never submit a variant other than the one the dropdowns show
    if (!this.#isSettled()) return;

    const form = this.#targetAddToCartButton.closest('form');
    const restoreQuantity = this.#lendQuantityToMainForm();

    // Required fields, such as a gift card recipient, block the submit. Point
    // the shopper at them instead of playing a success animation. Checked
    // after lending so the quantity being validated is the bar's own.
    if (form && !form.checkValidity()) {
      form.reportValidity();
      restoreQuantity();
      return;
    }

    const quantitySelector = this.#getMainQuantitySelector();
    const canAdd = quantitySelector?.canAddToCart ? quantitySelector.canAddToCart().canAdd : true;

    this.#targetAddToCartButton.dataset.puppet = 'true';
    try {
      // The click submits the main form, and everything that reads the
      // quantity (FormData, the queued add, validation) runs synchronously
      // inside it, so the main form can be put back straight afterwards.
      this.#targetAddToCartButton.click();
    } finally {
      restoreQuantity();
    }

    // Over the quantity limit: the main form shows its own error message
    if (!canAdd) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
      return;
    }

    const { addToCartButton, productImage } = this.refs;
    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    /** @type {Element[]} */
    const animatedElements = [addToCartButton];
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    // The product image is hidden on smaller screens; only fly it when it is on screen
    if (cartIcon && productImage && productImage.getClientRects().length > 0) {
      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = productImage;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);
      animatedElements.push(flyToCartElement);
    }

    await onAnimationEnd(animatedElements);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles a pick in one of the option dropdowns by driving the main variant picker
   * @param {Event} event
   */
  handleOptionChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const variant = this.#findVariantForSelection(Number(select.dataset.optionIndex));
    if (!variant) return;

    this.#requestedVariantId = String(variant.id);
    // When the exact combination doesn't exist, show the variant used instead
    this.#showVariantInSelects(this.#requestedVariantId);
    if (this.#variantChange) return;

    this.refs.addToCartButton.setAttribute('aria-busy', 'true');
    this.#variantChange = this.#applyRequestedVariant().finally(() => {
      this.#variantChange = null;
      if (!this.isConnected) return;

      // The walk ended without reaching the requested variant (an option could
      // not be set, or an update timed out), so show where the page really is
      const deferredEvent = this.#deferredVariantEvent;
      this.#deferredVariantEvent = null;
      if (deferredEvent) this.#applyVariantUpdate(deferredEvent);

      this.refs.addToCartButton.removeAttribute('aria-busy');

      const currentVariantId = this.dataset.currentVariantId || '';
      if (currentVariantId) this.#showVariantInSelects(currentVariantId);

      const pendingAddAt = this.#pendingAddAt;
      this.#pendingAddAt = 0;
      const reachedRequested = currentVariantId === this.#requestedVariantId;
      if (!pendingAddAt || !reachedRequested || Date.now() - pendingAddAt > PENDING_ADD_EXPIRY) return;

      // Let the product form finish handling the same variant update first. A tap
      // on add to bag never becomes a notify-me tap, or the other way round.
      const pendingNotify = this.#pendingNotify;
      setTimeout(() => {
        const button = this.refs.addToCartButton;
        const sameAction = (button.dataset.notifyMe === 'true') === pendingNotify;
        if (this.isConnected && !button.disabled && sameAction) this.handleAddToCartClick();
      });
    });
  };

  /**
   * Handles the quantity decrease button
   */
  decreaseQuantity = () => {
    this.#setQuantity(this.#currentQuantity - this.#getQuantityStep());
  };

  /**
   * Handles the quantity increase button
   */
  increaseQuantity = () => {
    this.#setQuantity(this.#currentQuantity + this.#getQuantityStep());
  };

  /**
   * Handles a quantity typed into the input
   */
  handleQuantityChange = () => {
    const { stickyQuantity } = this.refs;
    if (!stickyQuantity) return;

    this.#setQuantity(Number(stickyQuantity.value));
  };

  /**
   * Moves the main variant picker to the most recently requested variant.
   * The picker is changed one option at a time, waiting for each variant
   * update: changing several at once leaves the URL pointing at a stale
   * combination. A newer pick made mid-change restarts the walk.
   */
  async #applyRequestedVariant() {
    while (this.isConnected) {
      const requestedVariantId = this.#requestedVariantId;
      const optionValues = this.#getOptionValuesForVariant(requestedVariantId);
      if (!optionValues) return;

      for (let index = 0; index < optionValues.length; index++) {
        if (!this.isConnected || this.#requestedVariantId !== requestedVariantId) break;

        const picker = this.#getMainVariantPicker();
        const control = picker ? this.#findPickerControl(picker, index, optionValues[index] ?? '') : null;
        // Options with a single value render no control; nothing to change there
        if (!control || !control.needsChange) continue;

        const variantUpdated = this.#waitForVariantUpdate();
        control.select();
        await variantUpdated;
      }

      if (this.#requestedVariantId === requestedVariantId) return;
    }
  }

  /**
   * Whether the main form holds the variant the dropdowns show
   * @returns {boolean}
   */
  #isSettled() {
    if (this.#variantChange) return false;

    const selects = this.refs.optionSelects ?? [];
    const current = this.#getVariant(this.dataset.currentVariantId || '');
    // No dropdowns, or a combination with no variant (its button is disabled): nothing to compare
    if (selects.length === 0 || !current) return true;

    return selects.every((select) => select.value === current.options[Number(select.dataset.optionIndex)]);
  }

  /**
   * The product's variants, read from the bar's JSON and re-read after each re-render
   * @returns {VariantEntry[]}
   */
  #getVariants() {
    const text = this.refs.variantData?.textContent ?? '';
    if (text !== this.#variantCache.text) {
      /** @type {VariantEntry[]} */
      let variants = [];
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) variants = parsed;
      } catch {
        // An unreadable list leaves the dropdowns doing nothing
      }
      this.#variantCache = { text, variants };
    }
    return this.#variantCache.variants;
  }

  /**
   * @param {string} variantId
   * @returns {VariantEntry | undefined}
   */
  #getVariant(variantId) {
    return this.#getVariants().find((variant) => String(variant.id) === variantId);
  }

  /**
   * Reads a variant's option values
   * @param {string} variantId
   * @returns {string[] | null}
   */
  #getOptionValuesForVariant(variantId) {
    return this.#getVariant(variantId)?.options.map(String) ?? null;
  }

  /**
   * Finds the variant for the values chosen in the dropdowns. When that exact
   * combination doesn't exist, it keeps the value just picked and takes the
   * closest variant, preferring ones in stock.
   * @param {number} changedIndex - Option position of the dropdown that changed
   * @returns {VariantEntry | null}
   */
  #findVariantForSelection(changedIndex) {
    const variants = this.#getVariants();
    const first = variants[0];
    if (!first) return null;

    const current = this.#getVariant(this.dataset.currentVariantId || '');
    // Options with a single value have no dropdown, and every variant shares that value
    const chosen = first.options.map(
      (value, index) => this.#getOptionSelect(index)?.value ?? current?.options[index] ?? value
    );

    const exact = variants.find((variant) => variant.options.every((value, index) => value === chosen[index]));
    if (exact) return exact;

    const optionCount = first.options.length;
    /** @param {VariantEntry} variant */
    const score = (variant) =>
      (variant.available ? optionCount : 0) + variant.options.filter((value, index) => value === chosen[index]).length;

    return (
      variants
        .filter((variant) => variant.options[changedIndex] === chosen[changedIndex])
        .sort((a, b) => score(b) - score(a))[0] ?? null
    );
  }

  /**
   * @param {number} index - Option position
   * @returns {HTMLSelectElement | undefined}
   */
  #getOptionSelect(index) {
    return (this.refs.optionSelects ?? []).find((select) => Number(select.dataset.optionIndex) === index);
  }

  /**
   * Sets each dropdown to a variant's option values
   * @param {string} variantId
   */
  #showVariantInSelects(variantId) {
    const variant = this.#getVariant(variantId);
    if (!variant) return;

    for (const select of this.refs.optionSelects ?? []) {
      const value = variant.options[Number(select.dataset.optionIndex)];
      if (value !== undefined && Array.from(select.options).some((option) => option.value === value)) {
        select.value = value;
      }
    }
  }

  /**
   * Finds the main picker control for one option position, for both the
   * buttons/swatches style (radios) and the dropdowns style (selects)
   * @param {HTMLElement} picker
   * @param {number} index - Option position, the picker's data-fieldset-index
   * @param {string} value - Option value to select
   * @returns {{ needsChange: boolean, select: () => void } | null}
   */
  #findPickerControl(picker, index, value) {
    const radios = /** @type {HTMLInputElement[]} */ (
      Array.from(picker.querySelectorAll(`input[type="radio"][data-fieldset-index="${index}"]`))
    );

    if (radios.length > 0) {
      const radio = radios.find((input) => input.value === value);
      if (!radio) return null;

      return {
        needsChange: !radio.checked,
        select: () => {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        },
      };
    }

    const select = picker.querySelector(`select[id="Option-${picker.dataset.blockId}-${index}"]`);
    if (!(select instanceof HTMLSelectElement)) return null;
    if (!Array.from(select.options).some((option) => option.value === value)) return null;

    return {
      needsChange: select.value !== value,
      select: () => {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      },
    };
  }

  /**
   * Resolves on the next variant update in this section, or after a timeout
   * @returns {Promise<void>}
   */
  #waitForVariantUpdate() {
    return new Promise((resolve) => {
      const section = this.closest('.shopify-section');
      if (!section) {
        resolve();
        return;
      }

      const controller = new AbortController();
      const finish = () => {
        controller.abort();
        clearTimeout(timeout);
        resolve();
      };
      const timeout = setTimeout(finish, VARIANT_UPDATE_TIMEOUT);

      section.addEventListener(ThemeEvents.variantUpdate, finish, { signal: controller.signal });
      this.#abortController.signal.addEventListener('abort', finish, { signal: controller.signal });
    });
  }

  /**
   * Hands the bar's quantity to the main form for the duration of a click
   * @returns {() => void} Puts the main form back
   */
  #lendQuantityToMainForm() {
    const productForm = this.#getProductForm();
    const form = productForm?.querySelector('form');
    if (!productForm || !form) return () => {};

    const quantity = String(this.#getQuantityToAdd());
    /** @type {Array<() => void>} */
    const restores = [];

    // A visible main stepper is kept in step with the bar, so it is not restored
    const hasMainQuantitySelector = this.#syncMainQuantity(quantity);

    const quantityInput = form.querySelector('input[name="quantity"]');
    if (quantityInput instanceof HTMLInputElement) {
      if (!hasMainQuantitySelector) {
        const previousValue = quantityInput.value;
        quantityInput.value = quantity;
        restores.push(() => {
          quantityInput.value = previousValue;
        });
      }

      // A disabled input is left out of the submitted form data. The main
      // input is rendered disabled when the page loads on a sold-out variant
      // and the theme never re-enables it, so lift that just for this click.
      if (quantityInput.disabled) {
        quantityInput.disabled = false;
        restores.push(() => {
          quantityInput.disabled = true;
        });
      }
    } else {
      const quantityField = document.createElement('input');
      quantityField.type = 'hidden';
      quantityField.name = 'quantity';
      quantityField.value = quantity;
      form.append(quantityField);
      restores.push(() => quantityField.remove());
    }

    // An add queued during a variant change reads this instead of the form
    const previousDefault = productForm.dataset.quantityDefault;
    productForm.dataset.quantityDefault = quantity;
    restores.push(() => {
      if (previousDefault === undefined) {
        delete productForm.dataset.quantityDefault;
      } else {
        productForm.dataset.quantityDefault = previousDefault;
      }
    });

    return () => {
      restores.reverse().forEach((restore) => restore());
    };
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const variantId = event.detail.resource?.id ? String(event.detail.resource.id) : '';

    // Mid-walk the picker passes through in-between combinations, some sold out
    // or missing. Showing those would flash the wrong price and image and could
    // disable the button under the shopper's thumb, so only the requested
    // variant is shown. The latest in-between update is kept in case the walk
    // never gets there.
    if (this.#variantChange && variantId !== this.#requestedVariantId) {
      this.dataset.currentVariantId = variantId;
      this.#deferredVariantEvent = event;
      return;
    }

    this.#deferredVariantEvent = null;
    this.#applyVariantUpdate(event);
  };

  /**
   * Re-renders the bar from a variant update's server HTML
   * @param {CustomEvent} event - The variant update event
   */
  #applyVariantUpdate(event) {
    const variant = event.detail.resource;
    const html = event.detail.data.html;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart =
      html.querySelector(`sticky-add-to-cart[data-product-id="${this.dataset.productId}"]`) ??
      html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';

    // The value the server chose for each dropdown. Read before the morph, which
    // can move nodes out of the new markup and leaves a stale selected attribute
    // on an option the shopper had chosen earlier.
    const serverValues = new Map(
      Array.from(newStickyBar.querySelectorAll('select[data-option-index]'), (select) => [
        select.getAttribute('data-option-index') ?? '',
        /** @type {HTMLSelectElement} */ (select).value,
      ])
    );

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = newStickyAddToCart.dataset.variantAvailable;
    this.dataset.currentVariantId = variant?.id ? String(variant.id) : '';

    // Refs refresh on a microtask after a morph; refresh them now so the code
    // below works with the new nodes
    this.updatedCallback();

    // A morph leaves each select's chosen option as it was, so set it again. A
    // combination with no variant takes the values the server chose.
    const currentVariantId = this.dataset.currentVariantId;
    if (currentVariantId) {
      this.#showVariantInSelects(currentVariantId);
    } else {
      for (const select of this.refs.optionSelects ?? []) {
        const value = serverValues.get(select.dataset.optionIndex ?? '');
        if (value !== undefined) select.value = value;
      }
    }
    if (this.#variantChange) {
      this.refs.addToCartButton.setAttribute('aria-busy', 'true');
    }

    this.#targetAddToCartButton = this.#getMainAddToCartButton() ?? this.#targetAddToCartButton;

    // The morph resets the stepper to the server's minimum; keep the shopper's
    // quantity, fitted to the new variant's quantity rules
    this.#setQuantity(this.#currentQuantity, { syncMain: false });
    this.#syncNotifyMeButton();
  }

  /**
   * The page's back-in-stock block, when the theme shows one for this product
   * @returns {HTMLElement | null}
   */
  #getNotifyMeBlock() {
    return this.closest('.shopify-section')?.querySelector('.notify-me-block') ?? null;
  }

  /**
   * The back-in-stock form's entry for the bar's variant, if the form lists it
   * @param {HTMLElement | null} block
   * @returns {HTMLOptionElement | null}
   */
  #getNotifyMeOption(block) {
    const variantId = this.dataset.currentVariantId;
    if (!block || !variantId) return null;

    const option = block.querySelector(`[data-notify-variant] option[value="${CSS.escape(variantId)}"]`);
    return option instanceof HTMLOptionElement ? option : null;
  }

  /**
   * On a sold-out variant, turns the bar's button into the page's notify-me
   * button, with the same icon and words, instead of a disabled sold-out
   * button. The server always renders the sold-out button and every re-render
   * replaces it, so this runs after each one.
   */
  #syncNotifyMeButton() {
    const { addToCartButton } = this.refs;
    const block = this.#getNotifyMeBlock();
    const notifyButton = block?.querySelector('.notify-me-btn');
    const soldOut = Boolean(this.dataset.currentVariantId) && this.dataset.variantAvailable !== 'true';
    if (!soldOut || !notifyButton || addToCartButton.dataset.notifyMe === 'true') return;

    // The form only lists variants that were sold out when the page loaded. One
    // that sold out since keeps the disabled sold-out button, as the page does.
    if (!this.#getNotifyMeOption(block)) return;

    const label = notifyButton.textContent?.trim() ?? '';
    addToCartButton.dataset.notifyMe = 'true';
    addToCartButton.disabled = false;
    addToCartButton.setAttribute('aria-haspopup', 'dialog');
    if (label) addToCartButton.setAttribute('aria-label', label);

    const text = addToCartButton.querySelector('.add-to-cart-text__content > span');
    if (text && label) text.textContent = label;

    const icon = notifyButton.querySelector('svg');
    const textRow = addToCartButton.querySelector('.add-to-cart-text');
    if (icon && textRow) {
      const wrapper = document.createElement('span');
      wrapper.className = 'svg-wrapper sticky-add-to-cart__notify-icon';
      wrapper.setAttribute('aria-hidden', 'true');
      wrapper.append(icon.cloneNode(true));
      textRow.prepend(wrapper);
    }
  }

  /**
   * Opens the page's back-in-stock form for the variant the bar shows
   */
  #openNotifyMe() {
    const block = this.#getNotifyMeBlock();
    const notifyButton = block?.querySelector('.notify-me-btn');
    const option = this.#getNotifyMeOption(block);
    if (!block || !(notifyButton instanceof HTMLElement) || !option) return;

    // Point the form at the bar's variant directly; its change listener fills in
    // the hidden variant fields the signup sends
    const select = option.closest('select');
    if (select && select.value !== option.value) {
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // A hidden block can't show its popup
    if (block.dataset.variantId !== option.value) block.dataset.variantId = option.value;
    block.hidden = false;
    notifyButton.click();
  }

  /**
   * Resets the puppet flag and, for variants with a maximum, refits the
   * stepper to what the cart now holds
   * @param {CustomEvent} event - The cart update event
   */
  #handleCartUpdate = async (event) => {
    this.#handleCartAddComplete(event);

    const variantId = this.dataset.currentVariantId;
    if (!this.refs.stickyQuantity?.max || !variantId) return;

    let cart = event.detail?.resource;
    if (!Array.isArray(cart?.items)) {
      try {
        const response = await fetch(`${Theme.routes.cart_url}.js`);
        cart = await response.json();
      } catch {
        return;
      }
    }

    const { stickyQuantity } = this.refs;
    if (!stickyQuantity || !Array.isArray(cart?.items) || variantId !== this.dataset.currentVariantId) return;

    // A variant can sit on several lines, for example with different line item properties
    const cartQuantity = cart.items
      .filter((/** @type {{ variant_id: number }} */ item) => String(item.variant_id) === variantId)
      .reduce((/** @type {number} */ total, /** @type {{ quantity: number }} */ item) => total + item.quantity, 0);

    stickyQuantity.dataset.cartQuantity = String(cartQuantity);
    this.#setQuantity(this.#currentQuantity, { syncMain: false });
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Mirrors the main quantity selector into the bar
   * @param {CustomEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Ignore cart drawer lines and quantity selectors outside this product form
    if (event.detail.cartLine) return;

    const productForm = this.#getProductForm();
    if (!productForm || !(event.target instanceof Node) || !productForm.contains(event.target)) return;

    this.#setQuantity(Number(event.detail.quantity), { syncMain: false });
  };

  /**
   * Phones hide the stepper, so when it disappears the bar takes the quantity
   * the shopper can still see: the main quantity selector's, or the minimum
   * @param {MediaQueryListEvent} event
   */
  #handleBreakpointChange = (event) => {
    const mainQuantitySelector = this.#getMainQuantitySelector();

    // Back on a wider screen the stepper shows again; match the main quantity selector if there is one
    if (event.matches) {
      if (mainQuantitySelector) {
        this.#setQuantity(Number(mainQuantitySelector.getValue()) || this.#currentQuantity, { syncMain: false });
      }
      return;
    }

    const quantity = mainQuantitySelector
      ? Number(mainQuantitySelector.getValue())
      : Number(this.refs.stickyQuantity?.min) || 1;
    this.#setQuantity(quantity, { syncMain: false });
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  #getMainAddToCartButton() {
    return this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? null;
  }

  /**
   * Gets the main variant picker for this product
   * @returns {HTMLElement | null}
   */
  #getMainVariantPicker() {
    const sectionElement = this.closest('.shopify-section');
    return sectionElement?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`) ?? null;
  }

  /**
   * Gets the main quantity selector, when the product form shows one
   * @returns {QuantitySelector | null}
   */
  #getMainQuantitySelector() {
    const selector = this.#getProductForm()?.querySelector('quantity-selector-component');
    return selector && 'setValue' in selector ? /** @type {QuantitySelector} */ (selector) : null;
  }

  /**
   * Copies a quantity to the main quantity selector, if the form shows one
   * @param {string} quantity
   * @returns {boolean} Whether there was a main quantity selector
   */
  #syncMainQuantity(quantity) {
    const mainQuantitySelector = this.#getMainQuantitySelector();
    if (!mainQuantitySelector) return false;

    if (mainQuantitySelector.getValue() !== quantity) {
      mainQuantitySelector.setValue(quantity);
      // Tells listeners such as the price per item about the new quantity
      mainQuantitySelector.onQuantityChange?.();
    }
    mainQuantitySelector.updateButtonStates?.();
    return true;
  }

  /**
   * @returns {number}
   */
  #getQuantityStep() {
    return Number(this.refs.stickyQuantity?.step) || 1;
  }

  /**
   * The quantity a tap adds. Phones hide the stepper, and a shopper should never
   * add a quantity they cannot see, so there it is the main quantity selector's
   * value when that is on screen, and otherwise the variant's minimum.
   * @returns {number}
   */
  #getQuantityToAdd() {
    const { stickyQuantity } = this.refs;
    if (!stickyQuantity || stickyQuantity.getClientRects().length > 0) return this.#currentQuantity;

    const mainQuantitySelector = this.#getMainQuantitySelector();
    if (mainQuantitySelector && mainQuantitySelector.getClientRects().length > 0) {
      return Number(mainQuantitySelector.getValue()) || this.#currentQuantity;
    }

    return Number(stickyQuantity.min) || 1;
  }

  /**
   * The most that can still be added: the variant's maximum less what the cart
   * already holds, matching the main quantity selector
   * @param {number} min
   * @returns {number}
   */
  #getEffectiveMax(min) {
    const { stickyQuantity } = this.refs;
    if (!stickyQuantity?.max) return Infinity;

    const cartQuantity = Number(stickyQuantity.dataset.cartQuantity) || 0;
    return Math.max(Number(stickyQuantity.max) - cartQuantity, min);
  }

  /**
   * Sets the bar's quantity, fitted to the input's min, max and step
   * @param {number} value
   * @param {{ syncMain?: boolean }} [options] - Whether to copy the value to the main quantity selector
   */
  #setQuantity(value, { syncMain = true } = {}) {
    const { stickyQuantity } = this.refs;
    const min = Number(stickyQuantity?.min) || 1;
    const max = this.#getEffectiveMax(min);
    const step = this.#getQuantityStep();

    let quantity = Number.isFinite(value) ? Math.round(value) : min;
    quantity = Math.min(Math.max(quantity, min), max);
    // Snap down to the step, counting up from the minimum, as the main quantity selector does
    quantity = min + Math.floor((quantity - min) / step) * step;
    if (quantity > max) quantity -= step;
    quantity = Math.max(quantity, min);

    this.#currentQuantity = quantity;
    this.#renderQuantity(min, max);

    if (syncMain) this.#syncMainQuantity(String(quantity));
  }

  /**
   * Writes the current quantity into the stepper and updates its buttons
   * @param {number} min
   * @param {number} max
   */
  #renderQuantity(min, max) {
    const { stickyQuantity, stickyQuantityMinus, stickyQuantityPlus } = this.refs;
    if (stickyQuantity) stickyQuantity.value = String(this.#currentQuantity);
    if (stickyQuantityMinus) stickyQuantityMinus.disabled = this.#currentQuantity <= min;
    if (stickyQuantityPlus) stickyQuantityPlus.disabled = this.#currentQuantity >= max;
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
